import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { authService } from '@/services/authService'
import { clearStoredTokens } from '@/services/api'
import { setSentryUser } from '@/lib/sentry'

// ── Thunks ──────────────────────────────────────────────────────────────────

export const loginThunk = createAsyncThunk(
  'auth/login',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const data = await authService.login(email, password)
      return data
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Login failed')
    }
  }
)

export const logoutThunk = createAsyncThunk(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      await authService.logout()
    } catch {
      // silent — always clear local state
    }
  }
)

// ── Slice ────────────────────────────────────────────────────────────────────
// Tokens live in HTTP-only cookies — only user data kept in state/localStorage

const initialState = {
  user:      JSON.parse(localStorage.getItem('hg_admin_user') || 'null'),
  isLoading: false,
  error:     null,
}

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Keep setTokens as a no-op for any legacy callers — tokens are now in cookies
    setTokens() {},
    clearAuth(state) {
      state.user = null
      state.error = null
      localStorage.removeItem('hg_admin_user')
      clearStoredTokens()
    },
    clearError(state) {
      state.error = null
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(loginThunk.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        localStorage.setItem('hg_admin_user', JSON.stringify(action.payload.user))
        setSentryUser(action.payload.user) // attach identity to all future errors
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload
      })
    // Logout
    builder
      .addCase(logoutThunk.fulfilled, (state) => {
        state.user = null
        localStorage.removeItem('hg_admin_user')
        clearStoredTokens()
        setSentryUser(null) // clear identity on logout
      })
  },
})

export const { setTokens, clearAuth, clearError } = authSlice.actions
export default authSlice.reducer
